// Is wiznaibot (the Twitch betting bot) down? Decides the homepage's "wiznaibot is
// down" banner.
//
// The input is the bot's /health.json, fetched through the wiznaibot-health Function.
// The service showing "active" proves nothing: on 2026-09-25 the process kept running
// for three days after it lost Twitch chat. So this asks whether the bot is logged in to
// chat, whether anything is arriving, and, on the live bracket, whether it heard betting
// open for the matches being played. Only positive evidence counts. A missing or
// old-format answer is "unknown", so the banner can't cry wolf while the bot is fine.

// Logged out of chat this long. The bot retries within a minute, so this is a real outage.
export const OFFLINE_MS = 10 * 60 * 1000;
// Logged in, but nothing has arrived in this long. Twitch pings every ~5 minutes, and the
// bot's own watchdog reconnects at 10, so this only fires if that failed too.
export const QUIET_MS = 15 * 60 * 1000;
// Matches keep finishing (the tournament's LastMod) this long after the last "Betting is
// open" the bot heard. That covers a bot that's connected but deaf to FFTBG, e.g. banned
// from the channel or tripped up by a reworded message.
export const MISSED_MS = 30 * 60 * 1000;

// `status` = the Function's HTTP status, `health` = its parsed body (or null), `lastMod`
// = the live tournament's LastMod (an ISO string or ms; omit it off the live bracket).
// Returns null (fine, or unknown) or { kind, since }: kind is 'unreachable' (the bot's
// server didn't answer), 'offline', 'quiet' or 'missing'; since = ms, when known.
export function botDownReason({ status, health, lastMod } = {}) {
  if (status === 502 || status === 504) return { kind: 'unreachable', since: null };
  if (status !== 200 || !health || typeof health.connected !== 'boolean' || !health.now) return null;
  const now = health.now;  // the bot's own clock, so its timestamps compare cleanly
  if (!health.connected) {
    const since = health.since || health.startedAt;
    return since && now - since > OFFLINE_MS ? { kind: 'offline', since } : null;
  }
  if (health.lastDataAt && now - health.lastDataAt > QUIET_MS) return { kind: 'quiet', since: health.lastDataAt };
  const mod = typeof lastMod === 'string' ? Date.parse(lastMod) : lastMod;
  if (mod) {
    // A fresh login or restart starts the clock over.
    const heard = Math.max(health.lastOpenAt || 0, health.since || 0, health.startedAt || 0);
    if (mod - heard > MISSED_MS) return { kind: 'missing', since: health.lastOpenAt || null };
  }
  return null;
}
